import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useQuery } from '../api.js';
import { useSession } from '../session.js';
import { List, Row, Tile } from '../controls.js';
import { Drawer, ErrorNote, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';
import { BookIcon, CheckIcon, ClockIcon, GraduationIcon, PlaneIcon } from '../icons.js';
import type { IacraTotals, LogbookEntry, LogbookTimes, LogbookTotals } from '../../../shared/types.js';

interface Book {
  holder: { name: string; kind: string; subtitle: string };
  totals: LogbookTotals;
  iacra: IacraTotals;
  entries: LogbookEntry[];
}

/** How many flights the book opens on. The rest are one click away. */
const RECENT = 5;

const hrs = (n: number): string => n.toFixed(1);
/** A logbook column shows a figure in tenths, or a dash when nothing was logged. */
const cell = (n: number): string => (n > 0 ? n.toFixed(1) : '—');

/**
 * The logbook. Every entry came from a lesson being closed out, so the book and
 * the schedule cannot disagree — there is no way to type an entry directly.
 */
export function Logbook({ studentId, embedded }: { studentId?: number; embedded?: boolean }): JSX.Element {
  const { role } = useSession();
  const path = studentId ? `/logbook/student/${studentId}` : '/logbook';
  const { data, error, reload } = useQuery<Book>(path);
  const [openId, setOpenId] = useState<number | null>(null);
  // Entries come back newest first. Only the last few are worth scrolling past
  // on the way to the totals, so the rest start folded away.
  const [showAll, setShowAll] = useState(false);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const t = data.totals;
  const isInstructorBook = data.holder.kind === 'instructor';
  const shown = showAll ? data.entries : data.entries.slice(0, RECENT);
  const hidden = data.entries.length - shown.length;

  const body = (
    <div className="stack" style={{ gap: 20 }}>
      <div className="card">
        <div className="card__body">
          <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <Tile color={isInstructorBook ? 'purple' : 'blue'} size="lg">
              {isInstructorBook ? <GraduationIcon /> : <BookIcon />}
            </Tile>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em' }}>
                {data.holder.name}
              </span>
              <span className="small muted">{data.holder.subtitle}</span>
            </span>
            <span style={{ marginLeft: 'auto', textAlign: 'right', flex: 'none' }}>
              <span style={{ display: 'block', fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {hrs(t.totalTime)}
              </span>
              <span className="small muted">total hours</span>
            </span>
          </div>
        </div>
      </div>

      {data.entries.length === 0 ? (
        <div className="card">
          <div className="empty">
            <BookIcon style={{ width: 30, height: 30, opacity: 0.4, marginBottom: 10 }} />
            <div style={{ fontWeight: 600, color: 'var(--label-1)' }}>Nothing logged yet</div>
            <div style={{ marginTop: 4, maxWidth: 400, marginInline: 'auto', textWrap: 'pretty' }}>
              Entries are written when a lesson is closed out, so the book always matches what
              actually flew.
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Wide: the classic logbook page. Narrow: one card per flight. */}
          <div className="card logbook__table">
            <div className="table__scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>Aircraft</th><th>Route</th>
                    <th className="num">Total</th>
                    <th className="num">{isInstructorBook ? 'Given' : 'Dual'}</th>
                    <th className="num">PIC</th>
                    <th className="num">XC</th>
                    <th className="num">Night</th>
                    <th className="num">Inst</th>
                    <th className="num">Ground</th>
                    <th className="num">Ldg</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((e) => (
                    <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(e.id)}>
                      <td className="bold">{e.flownOn}</td>
                      <td>{e.isGround || e.times.totalTime === 0
                        ? <span className="muted">Ground</span>
                        : <>{e.aircraftTail} <span className="muted">{e.aircraftType}</span></>}</td>
                      <td className="muted">{e.isGround || e.times.totalTime === 0 ? '—' : `${e.fromAirport}–${e.toAirport}`}</td>
                      <td className="num">{cell(e.times.totalTime)}</td>
                      <td className="num">{cell(isInstructorBook ? e.times.instructionGiven : e.times.dualReceived)}</td>
                      <td className="num">{cell(e.times.pic)}</td>
                      <td className="num">{cell(e.times.crossCountry)}</td>
                      <td className="num">{cell(e.times.night)}</td>
                      <td className="num">{cell(e.times.actualInstrument + e.times.simulatedInstrument)}</td>
                      <td className="num">{cell(e.times.groundInstruction)}</td>
                      <td className="num">{e.counts.dayLandings + e.counts.nightLandings || '—'}</td>
                      <td className="num"><span className="btn btn--link">Open</span></td>
                    </tr>
                  ))}
                  {(hidden > 0 || showAll) && (
                    <tr className="logbook__more">
                      <td colSpan={12}>
                        <button className="btn btn--link" onClick={() => setShowAll((v) => !v)}>
                          {hidden > 0
                            ? `Show ${hidden} earlier ${hidden === 1 ? 'entry' : 'entries'}`
                            : `Show only the last ${RECENT}`}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="logbook__totals">
                    <td className="bold">Totals</td>
                    <td colSpan={2}>
                      {t.entries} {t.entries === 1 ? 'entry' : 'entries'}
                    </td>
                    <td className="num">{hrs(t.totalTime)}</td>
                    <td className="num">{hrs(isInstructorBook ? t.instructionGiven : t.dualReceived)}</td>
                    <td className="num">{hrs(t.pic)}</td>
                    <td className="num">{hrs(t.crossCountry)}</td>
                    <td className="num">{hrs(t.night)}</td>
                    <td className="num">{hrs(t.actualInstrument + t.simulatedInstrument)}</td>
                    <td className="num">{hrs(t.groundInstruction)}</td>
                    <td className="num">{t.dayLandings + t.nightLandings}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="logbook__cards">
            <List header={showAll
              ? `${data.entries.length} entries`
              : `Last ${shown.length} of ${data.entries.length} entries`}>
              {shown.map((e) => (
                <Row
                  key={e.id}
                  tile={<Tile color={e.isGround ? 'gray' : 'blue'}>{e.isGround ? <ClockIcon /> : <PlaneIcon />}</Tile>}
                  title={e.isGround || e.times.totalTime === 0
                    ? 'Ground instruction'
                    : `${e.aircraftTail} · ${e.aircraftType}`}
                  sub={`${e.flownOn}${e.isGround || e.times.totalTime === 0 ? '' : ` · ${e.fromAirport}–${e.toAirport}`}`}
                  value={`${hrs(e.times.totalTime > 0 ? e.times.totalTime : e.times.groundInstruction)} hrs`}
                  onClick={() => setOpenId(e.id)}
                  chevron
                />
              ))}
            </List>
            {(hidden > 0 || showAll) && (
              <div style={{ padding: '10px 4px 0', textAlign: 'center' }}>
                <button className="btn btn--link" onClick={() => setShowAll((v) => !v)}>
                  {hidden > 0
                    ? `Show ${hidden} earlier ${hidden === 1 ? 'entry' : 'entries'}`
                    : `Show only the last ${RECENT}`}
                </button>
              </div>
            )}
            <div className="logbook__cardtotals">
              <span>Totals · {t.entries} {t.entries === 1 ? 'entry' : 'entries'}</span>
              <strong>{hrs(t.totalTime)} hrs</strong>
            </div>
            <div className="logbook__cardgrid">
              {([
                [isInstructorBook ? 'Instruction given' : 'Dual received',
                  isInstructorBook ? t.instructionGiven : t.dualReceived],
                ['Pilot in command', t.pic],
                ['Cross-country', t.crossCountry],
                ['Night', t.night],
                ['Instrument', t.actualInstrument + t.simulatedInstrument],
                ['Ground', t.groundInstruction],
              ] as [string, number][]).map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{hrs(value)}</strong>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {data.iacra && data.entries.length > 0 && <IacraPanel t={data.iacra} />}

      {data.entries.length >= 200 && (
        <p className="note" style={{ marginTop: 0 }}>
          The most recent 200 entries are listed. The totals cover the whole logbook.
        </p>
      )}

      <p className="note" style={{ marginTop: 0 }}>
        Entries are created when a lesson is closed out. Times cannot be typed in directly — a wrong
        figure is corrected by closing the lesson out again, which rewrites the entry rather than
        adding a second one.
      </p>

      {openId && <EntrySheet id={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </div>
  );

  if (embedded) return body;

  return (
    <div className="page" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {role !== 'student' && <BookPicker current={studentId} />}
      {body}
    </div>
  );
}

/**
 * The totals an FAA Form 8710 asks for, in the order IACRA puts them on screen.
 * Several are cross-tabulations — cross-country flown as dual, night flown as
 * pilot in command — which a plain column sum cannot answer, so they are
 * computed per flight from the role that flight was logged in.
 */
function IacraPanel({ t }: { t: IacraTotals }): JSX.Element {
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const groups: { title: string; note?: string; rows: [string, number, boolean?][] }[] = [
    {
      title: 'Flight time',
      rows: [
        ['Total time', t.total],
        ['Instruction received', t.instructionReceived],
        ['Solo', t.solo],
        ['Pilot in command', t.pic],
        ['Second in command', t.sic],
        ['Number of flights', t.flights, true],
      ],
    },
    {
      title: 'Cross-country',
      note: 'The 8710 asks for cross-country split by the capacity it was flown in.',
      rows: [
        ['Cross-country total', t.crossCountryTotal],
        ['· instruction received', t.crossCountryInstructionReceived],
        ['· solo', t.crossCountrySolo],
        ['· pilot in command', t.crossCountryPic],
        ['· second in command', t.crossCountrySic],
      ],
    },
    {
      title: 'Night',
      rows: [
        ['Night total', t.nightTotal],
        ['· instruction received', t.nightInstructionReceived],
        ['· pilot in command', t.nightPic],
        ['· solo', t.nightSolo],
        ['Night takeoffs', t.nightTakeoffs, true],
        ['Night landings', t.nightLandings, true],
      ],
    },
    {
      title: 'Instrument',
      rows: [
        ['Actual instrument', t.instrumentActual],
        ['Simulated instrument', t.instrumentSimulated],
        ['· instruction received', t.instrumentInstructionReceived],
        ['Flight simulator / FTD / ATD', t.groundTrainer],
        ['Instrument approaches', t.approaches, true],
        ['Holding procedures', t.holds, true],
      ],
    },
    {
      title: 'Landings and ground',
      rows: [
        ['Day landings', t.dayLandings, true],
        ['Total landings', t.totalLandings, true],
        ['Ground instruction received', t.groundInstruction],
      ],
    },
  ];

  const copy = async () => {
    const text = groups
      .map((g) => `${g.title}\n` + g.rows.map(([label, value, isCount]) =>
        `  ${label}: ${isCount ? value : value.toFixed(1)}`).join('\n'))
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.ok('Totals copied', 'Paste them alongside your IACRA application.');
    } catch {
      toast.info('Copy from the screen', 'The browser refused clipboard access.');
    }
  };

  return (
    <div className="card">
      <div className="card__head">
        <span>
          <span className="card__title">Totals for IACRA</span>
          <span className="card__meta" style={{ display: 'block', marginTop: 2 }}>
            Every figure FAA Form 8710 asks for, computed from the entries above.
          </span>
        </span>
        <span className="row" style={{ gap: 8 }}>
          <button className="btn btn--sm" onClick={copy}>Copy</button>
          <button className="btn btn--sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Show'}
          </button>
        </span>
      </div>

      {open && (
        <div className="iacra">
          {groups.map((g) => (
            <section className="iacra__group" key={g.title}>
              <h4 className="iacra__title">{g.title}</h4>
              {g.rows.map(([label, value, isCount]) => (
                <div className="iacra__row" key={label}>
                  <span className={label.startsWith('·') ? 'iacra__sub' : undefined}>{label}</span>
                  <span className="iacra__v">{isCount ? value : value.toFixed(1)}</span>
                </div>
              ))}
              {g.note && <p className="iacra__note">{g.note}</p>}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Instructors and management switch between their own book and their students'. */
function BookPicker({ current }: { current?: number }): JSX.Element | null {
  const { data } = useQuery<{ holders: { id: number; name: string; entries: number; total: number }[] }>('/logbook/holders');
  const navigate = useNavigate();
  if (!data?.holders.length) return null;
  return (
    <div className="row" style={{ marginBottom: 14 }}>
      <span className="small muted">Viewing</span>
      <select className="select select--inline" value={current ?? ''}
        onChange={(e) => navigate(e.target.value ? `/logbook?student=${e.target.value}` : '/logbook')}>
        <option value="">My logbook</option>
        {data.holders.map((h) => (
          <option key={h.id} value={h.id}>{h.name} · {h.total.toFixed(1)} hrs</option>
        ))}
      </select>
    </div>
  );
}

/** The hour columns of an entry, in the order the logbook page lays them out. */
const TIME_ROWS: { key: keyof LogbookTimes; label: string }[] = [
  { key: 'totalTime', label: 'Total time of flight' },
  { key: 'pic', label: 'Pilot in command' },
  { key: 'sic', label: 'Second in command' },
  { key: 'solo', label: 'Solo' },
  { key: 'dualReceived', label: 'Dual received' },
  { key: 'instructionGiven', label: 'Instruction given' },
  { key: 'crossCountry', label: 'Cross-country' },
  { key: 'night', label: 'Night' },
  { key: 'actualInstrument', label: 'Actual instrument' },
  { key: 'simulatedInstrument', label: 'Simulated instrument' },
  { key: 'groundTrainer', label: 'Simulator / FTD' },
  { key: 'groundInstruction', label: 'Ground instruction' },
];

/** One fact of the flight: label above value, sitting in the entry grid. */
function Cell({ k, v, wide }: { k: string; v: string | number; wide?: boolean }): JSX.Element {
  return (
    <div className={wide ? 'entry__cell entry__cell--wide' : 'entry__cell'}>
      <span className="entry__k">{k}</span>
      <span className="entry__v">{v}</span>
    </div>
  );
}

function EntrySheet({ id, onClose, onChanged }: {
  id: number; onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const { data, reload } = useQuery<LogbookEntry>(`/logbook/entries/${id}`);
  const { role, can } = useSession();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [remarks, setRemarks] = useState('');
  // Hours are held as typed, so a half-finished figure is not rounded away.
  const [times, setTimes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const canAmend = role === 'management' || role === 'instructor';

  const beginAmend = (entry: LogbookEntry) => {
    setRemarks(entry.remarks);
    setTimes(Object.fromEntries(TIME_ROWS.map((r) => [r.key, String(entry.times[r.key])])));
    setProblems([]);
    setEditing(true);
  };

  // An entry carrying no flight time — a CFI's ground hours beside a student's
  // solo — is amended by the ground rules, whatever the lesson was called.
  const groundOnly = !!data && (data.isGround || data.times.totalTime === 0);

  const hours = (raw: string): number => {
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) / 10 : 0;
  };

  // The arithmetic the server enforces, mirrored so the button explains itself.
  const localProblems: string[] = [];
  if (editing && data) {
    const total = hours(times.totalTime ?? '0');
    if (groundOnly) {
      if (hours(times.groundInstruction ?? '0') <= 0) localProblems.push('Enter the ground instruction time.');
    } else {
      if (total <= 0) localProblems.push('Total time of flight must be more than zero.');
      for (const r of TIME_ROWS) {
        if (r.key === 'totalTime' || r.key === 'groundTrainer' || r.key === 'groundInstruction') continue;
        if (hours(times[r.key] ?? '0') > total) localProblems.push(`${r.label} cannot exceed the total time of flight.`);
      }
      if (hours(times.solo ?? '0') > 0 && hours(times.dualReceived ?? '0') > 0) {
        localProblems.push('A flight is either solo or dual received, not both.');
      }
    }
  }

  const save = async () => {
    setBusy(true);
    setProblems([]);
    try {
      await api.patch(`/logbook/entries/${id}`, {
        remarks,
        ...Object.fromEntries(rows.map((r) => [r.key, hours(times[r.key] ?? '0')])),
      });
      toast.ok('Entry amended');
      setEditing(false);
      reload();
      onChanged();
    } catch (err) {
      const details = (err as { details?: string[] }).details;
      setProblems(details?.length ? details : [err instanceof Error ? err.message : 'Something went wrong.']);
      toast.error('Could not amend', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  void can;

  // Reading, only the columns that carry time. Amending, every column this kind
  // of entry can hold — so a figure left at zero can be put right — and none it
  // cannot: a ground entry has no flight time, and a book records either
  // instruction received or instruction given, never both.
  const amendable = (key: keyof LogbookTimes): boolean => {
    if (!data) return false;
    if (groundOnly) return key === 'groundInstruction' || key === 'groundTrainer';
    return key !== (data.loggedAs === 'instructor' ? 'dualReceived' : 'instructionGiven');
  };
  const rows = data
    ? TIME_ROWS.filter((r) => (editing ? amendable(r.key) : data.times[r.key] > 0))
    : [];

  return (
    <Drawer
      title={data ? (data.isGround ? 'Ground instruction' : `${data.aircraftTail} · ${data.aircraftType}`) : 'Entry'}
      subtitle={data ? new Date(`${data.flownOn}T12:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
      }) : undefined}
      onClose={onClose}
      footer={canAmend && data && (
        editing
          ? (
            <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <button className="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn--primary" style={{ flex: 1 }}
                disabled={busy || localProblems.length > 0} onClick={save}>
                {busy ? 'Saving…' : 'Save amendment'}
              </button>
            </div>
          )
          : <button className="btn" onClick={() => beginAmend(data)}>Amend entry</button>
      )}
    >
      {!data && <Spinner />}
      {data && (
        <Panes>
          <Pane title="The flight" flush>
            <div className="row" style={{ gap: 6, padding: '12px 24px 0' }}>
              <Pill tone={data.loggedAs === 'instructor' ? 'blue' : 'green'}>
                {data.loggedAs === 'instructor' ? 'Instruction given' : 'Student entry'}
              </Pill>
              {data.isGround && <Pill tone="gray">Ground</Pill>}
            </div>
            {!data.isGround && (
              <div className="entry__grid">
                <Cell k="Route" v={`${data.fromAirport}–${data.toAirport}`} />
                {data.hobbsStart != null && data.hobbsEnd != null && (
                  <Cell k="Hobbs" v={`${data.hobbsStart.toFixed(1)} → ${data.hobbsEnd.toFixed(1)}`} />
                )}
                <Cell k="Day landings" v={data.counts.dayLandings} />
                <Cell k="Night landings" v={data.counts.nightLandings} />
                <Cell k="Approaches" v={data.counts.approaches} />
                <Cell k="Holds" v={data.counts.holds} />
                {data.route && <Cell k="Via" v={data.route} wide />}
              </div>
            )}
          </Pane>

          <Pane title="Time logged" flush>
            <div className="entry__grid">
              {rows.map((r) => (
                <label className="entry__cell" key={r.key}>
                  <span className="entry__k">{r.label}</span>
                  {editing ? (
                    <input
                      className="input closeout__num"
                      inputMode="decimal"
                      aria-label={r.label}
                      value={times[r.key] ?? ''}
                      onChange={(e) => setTimes((t) => ({ ...t, [r.key]: e.target.value }))}
                    />
                  ) : (
                    <span className="entry__v">{hrs(data.times[r.key])}</span>
                  )}
                </label>
              ))}
            </div>
            {editing && (
              <p className="small muted" style={{ margin: 0, padding: '0 24px 14px', textWrap: 'pretty' }}>
                No column may exceed the total time of flight. Amending here corrects this book
                only — the other pilot's entry keeps the figures it was written with.
              </p>
            )}
            {editing && (problems.length > 0 || localProblems.length > 0) && (
              <div className="auth__error" role="alert" style={{ margin: '0 24px 14px' }}>
                <span>
                  {(problems.length ? problems : localProblems).map((x) => (
                    <span key={x} style={{ display: 'block' }}>{x}</span>
                  ))}
                </span>
              </div>
            )}
          </Pane>

          <Pane title="Remarks">
            {editing ? (
              <textarea className="textarea" value={remarks} onChange={(e) => setRemarks(e.target.value)} autoFocus />
            ) : (
              <p style={{ margin: 0, textWrap: 'pretty' }}>
                {data.remarks || <span className="muted">No remarks recorded.</span>}
              </p>
            )}
            {data.endorsement && (
              <div style={{
                marginTop: 12, padding: '11px 13px', borderRadius: 'var(--r-md)',
                background: 'var(--tint-blue)', fontSize: 13, textWrap: 'pretty',
              }}>
                <strong style={{ display: 'block', marginBottom: 3 }}>Endorsement</strong>
                {data.endorsement}
              </div>
            )}
            {data.signature && (
              <div className="row" style={{ gap: 10, flexWrap: 'nowrap', alignItems: 'flex-start', marginTop: 16 }}>
                <Tile color="green"><CheckIcon /></Tile>
                <span>
                  <span style={{ display: 'block', fontWeight: 600 }}>Certified by {data.signature.name}</span>
                  <span className="small muted">
                    {data.signature.certificate || 'Certificate number not on file'} ·
                    {' '}{new Date(data.signature.at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </span>
              </div>
            )}
          </Pane>
        </Panes>
      )}
    </Drawer>
  );
}
